export interface AIEnhanceFeature {
  name: string;
  description: string;
  isCore: boolean;
  impact: 'high' | 'medium' | 'low';
}

export interface AIEnhanceResult {
  enhancedDescription: string;
  features: AIEnhanceFeature[];
  error: string | null;
}

/**
 * Calls the secure Edge Function to enhance a project description
 * The API key is handled entirely server-side
 * @param projectDescription The user's raw project description
 */
export async function aiEnhanceDescription(
  projectDescription: string,
  features?: Array<{ name: string; description?: string }>
): Promise<AIEnhanceResult> {
  if (!projectDescription.trim()) {
    return {
      enhancedDescription: '',
      features: [],
      error: 'Project description cannot be empty.'
    };
  }

  try {
    // Get the Supabase URL from environment variables
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not configured');
    }

    // Call our Edge Function with the project description and optional features
    const response = await fetch(`${supabaseUrl}/functions/v1/enhance-project-description`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // The anon key is safe to expose - it just gives public access to the function
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        projectDescription,
        features: features || []
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Edge Function error: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    
    return {
      enhancedDescription: result.enhancedDescription,
      features: result.features,
      error: result.error
    };
  } catch (error) {
    console.error('Error in AI enhancement:', error);
    return {
      enhancedDescription: '',
      features: [],
      error: error instanceof Error ? error.message : 'An unknown error occurred'
    };
  }
}
